/**
 * 메시지 본문에서 URL을 찾는 규칙.
 *
 * ⚠ 이 규칙은 서버 `server/services/linkPreview.js` 와 **의도적 중복**이다.
 *   Dockerfile 2단계가 런타임 이미지에 src/를 넣지 않고(COPY server ./server 뿐),
 *   server/ 만 볼륨 마운트라 모듈을 공유하는 것이 물리적으로 불가능하다.
 *   **한쪽을 고치면 반드시 다른 쪽도 고칠 것** — 어긋나면 말풍선 안의 링크와
 *   그 아래 미리보기 카드가 서로 다른 주소를 가리킨다.
 */

const URL_GLOBAL_RE = /https?:\/\/[^\s<>"'`]+/gi;
const TRAILING_PUNCT = /[.,;:!?"'、。，．)\]}>」』】]+$/;

/**
 * URL 뒤에 붙은 문장부호를 떼어낸다.
 * `참고: https://ex.com/a.` 에서 마지막 마침표까지 URL로 잡으면 링크가 404가 되고,
 * 서버가 계산한 미리보기 해시와도 어긋난다.
 * 단, URL 안에 여는 괄호가 더 많으면 짝이 맞는 닫는 괄호 1개는 되돌린다
 * (위키백과의 `..._(동음이의)` 같은 주소).
 */
export function trimTrailingPunctuation(raw: string): string {
  let u = String(raw).replace(TRAILING_PUNCT, '');
  const open = (u.match(/\(/g) || []).length;
  const close = (u.match(/\)/g) || []).length;
  if (open > close && String(raw)[u.length] === ')') u += ')';
  return u;
}

export type LinkifySegment = { type: 'text' | 'url'; value: string };

/**
 * 본문을 텍스트/URL 조각으로 나눈다. 떼어낸 문장부호는 뒤따르는 텍스트 조각에 남으므로
 * 화면에 보이는 글자는 원문과 완전히 같다.
 */
export function splitByUrl(text: string): LinkifySegment[] {
  const out: LinkifySegment[] = [];
  if (!text) return out;
  let last = 0;
  let m: RegExpExecArray | null;
  URL_GLOBAL_RE.lastIndex = 0;
  while ((m = URL_GLOBAL_RE.exec(text)) !== null) {
    const url = trimTrailingPunctuation(m[0]);
    if (!url) continue;
    if (m.index > last) out.push({ type: 'text', value: text.slice(last, m.index) });
    out.push({ type: 'url', value: url });
    last = m.index + url.length;
  }
  if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
  return out;
}

/** 미리보기 대상이 되는 첫 URL (서버 판정과 같은 규칙 — 메시지당 1개) */
export function extractFirstUrl(text: string): string | null {
  const first = splitByUrl(text).find((seg) => seg.type === 'url');
  return first ? first.value : null;
}
